<script setup lang="ts">
import { useElementSize } from "@vueuse/core";
import { nextTick, onBeforeUnmount, onMounted, shallowRef, watch } from "vue";
import { uuid } from "./live-player";
// 萤石云播放器
defineOptions({
  name: "YsPlayer",
});

// 传值方式
// 1、传url、accessToken
// 2、传deviceSerial、channel、accessToken
const props = defineProps<{
  // 播放地址
  // 高清：ezopen://open.ys7.com/${设备序列号}/${通道号}.hd.live
  // 流畅：ezopen://open.ys7.com/${设备序列号}/${通道号}.live
  url?: string;
  // 设备序列号
  deviceSerial?: string;
  // 通道号
  channel?: number;
  // 清晰度,默认为空，也表示高清
  // hd：高清；sd：流畅
  quality?: "hd" | "sd" | "";
  accessToken: string;
  // 播放器模板ID
  playerTemplate?: string;
}>();

// 播放器模板ID
// 可在萤石云控制台查看、修改、获取
// 位置：云直播-轻应用-特致珈默认模板
const PLAYER_TEMPLATE = "5f295a9341a74d439b5d69374c90d501";
const id = uuid();
let playerInstance: any = null;
let errorMsg = "";
const ysRef = shallowRef();
const { width, height } = useElementSize(ysRef);
let ratio = 16 / 9;
let isCurrentFullscreen = false;

onMounted(() => play());

onBeforeUnmount(() => destroy());

defineExpose({ play, destroy, resize });

// 防抖函数
function debounce(func: Function, wait: number) {
  let timeout: ReturnType<typeof setTimeout> | null = null;
  return function (this: any, ...args: any[]) {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(this, args), wait);
  };
}

// 加上防抖的 resize 函数
const debouncedResize = debounce(resize, 300);

watch(
  () => [width.value, height.value, ratio],
  () => {
    if (!isCurrentFullscreen) {
      debouncedResize();
    }
  },
);

function play(options?: Record<string, any>) {
  if (!props.accessToken) {
    errorMsg = `params 'accessToken' is not found`;
  } else {
    const url = formatterUrl();
    if (url) {
      createPlayerDom();
      nextTick(() => createPlayer(options));
    }
  }
}
function destroy() {
  if (playerInstance) {
    playerInstance.destroy();
    playerInstance = null;
    removePlayerDom();
  }
}
function resize() {
  // 根据播放器宽高比，设置播放器高度
  if (width.value > 0 && height.value > 0) {
    let calculatedWidth = width.value;
    let calculatedHeight = calculatedWidth / ratio;
    if (calculatedHeight > height.value) {
      calculatedHeight = height.value;
      calculatedWidth = calculatedHeight * ratio;
    }
    if (playerInstance?.resize) {
      playerInstance.resize(calculatedWidth, calculatedHeight);
    } else {
      const dom = document.getElementById(id);
      if (dom) {
        dom.style.width = `${calculatedWidth}px`;
        dom.style.height = `${calculatedHeight}px`;
      }
    }
  }
}

/**
 * 创建萤石云播放器实例，播放萤石云直播流
 */
function createPlayer(options?: Record<string, any>) {
  playerInstance = new window.EZUIKit.EZUIKitPlayer({
    id,
    url: formatterUrl(),
    accessToken: props.accessToken,
    template: props.playerTemplate || PLAYER_TEMPLATE,
    width: width.value,
    height: height.value,
    audio: 1,
    // @ts-ignore
    loggerOptions: {
      name: "YSPLAYER",
      level: "ERROR",
      showTime: true,
    },
    staticPath: "/plugins/ezuikit/ezuikit_static",
    ...options,
  });

  playerInstance.eventEmitter.on("fullscreen", () => {
    isCurrentFullscreen = true;
  });
  playerInstance.eventEmitter.on("exitFullscreen", () => {
    isCurrentFullscreen = false;
  });
  playerInstance.eventEmitter.on("videoInfo", (args: any) => {
    const { width: w, height: h } = args;
    // 计算播放器宽高比
    if (w && h) {
      ratio = w / h;
    }
  });
}

function formatterUrl(): string {
  if (props.url) {
    return props.url;
  } else {
    if (props.deviceSerial && props.channel) {
      const quality =
        props.quality === "" || props.quality === "hd" ? "hd." : "";
      return `ezopen://open.ys7.com/${props.deviceSerial}/${props.channel}.${quality}live`;
    } else {
      errorMsg = `params 'deviceSerial' or 'channel' is not found`;
      return "";
    }
  }
}

// 移除播放器DOM
function removePlayerDom() {
  const dom = document.getElementById(id);
  dom && dom.remove();
}
// 创建播放器DOM
function createPlayerDom() {
  if (!errorMsg) {
    removePlayerDom();
    const dom = document.createElement("div");
    dom.id = id;
    dom.style.width = "100%";
    dom.style.height = "100%";
    ysRef.value?.appendChild(dom);
  }
}
</script>

<template>
  <div ref="ysRef" class="i-player-bg i-player-container">
    <div
      v-if="errorMsg"
      class="i-player__error"
      style="color: red; font-weight: bold"
    >
      {{ errorMsg }}
    </div>
  </div>
</template>
